import { gql } from '@apollo/client/core'
import {
  CreateNotificationDto,
  NotificationGetArgs,
  NotificationUnsubArgs,
  UnreadCountDto,
  UpdateNotificationDto,
  UserNotificationDto,
  UserNotificationPagedResultDto
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useBaseCrud } from '../../composables/useBaseCrud'

/**
 * Replaces `NotificationService`.
 *
 * `create`/`update`/`delete` stay the three `throw new Error('Not implemented')` stubs the class had.
 * Notifications are raised by the API and never written from a client, and nothing calls them — but
 * this service fills `BaseNotificationMixin`'s `crudService` slot, whose type is
 * `BaseCrudServiceInterface` from the root of the mixin chain, so dropping them fails to compile.
 * They go when stage 7 replaces that chain with `useList`, which needs only `get` and `globalLoader`.
 *
 * The deletion path callers actually use is `deleteNotificationQueueItem`: it removes the item from
 * the recipient's queue and leaves the notification itself alone.
 */

const notificationFragments = {
  notification: gql`
    fragment NotificationFragment on UserNotificationDto {
      id,
      status,
      statusDate,
      state,
      nameOfInstigator,
      parsedMessage,
      parsedDescription,
      to,
      notification {
        id,
        code
      },
      arc {
        id,
        claims {
          id
        }
      },
      book {
        id
        title,
        bookCover
      },
      arcClaim {
        id,
        arc {
          id
        }
      }
      arcReviewSite {
        id,
        site,
        review {
          claimId
        }
      },
      arcReview {
        id,
        rating,
        claim {
          id,
          arc {
            id
          }
        }
      },
      arcTeam {
        id,
        name
      },
      arcTeamMember {
        id,
        arcTeam {
          id,
          name
        }
      }
    }
  `
}

const crud = useBaseCrud<UserNotificationDto>('notification')

const get = (args: NotificationGetArgs): Promise<UserNotificationPagedResultDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetNotifications (
        $notificationCodes: [String!],
        $unread: Boolean,
        $genericSearchTerm: String,
        $skip: Int,
        $take: Int
      ) {
        notifications (
          notificationCodes: $notificationCodes,
          unread: $unread,
          genericSearchTerm: $genericSearchTerm,
          skip: $skip,
          take: $take
        ) {
          totalRows,
          unreadNotificationCount,
          items {
            ...NotificationFragment
          }
        }
      }
      ${notificationFragments.notification}
    `
  })
}

const getById = (id?: number): Promise<UserNotificationDto> => {
  if (id === void 0) {
    return Promise.reject(new Error('Missing id.'))
  }

  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query GetNotification (
        $id: Int!
      ) {
        notification (id: $id) {
          ...NotificationFragment
        }
      }
      ${notificationFragments.notification}
    `
  })
}

const markAsRead = (notificationQueueItemId: number): Promise<number> => {
  return useApollo().mutate({
    variables: {
      id: notificationQueueItemId
    },
    mutation: gql`
      mutation MarkNotificationQueueItemAsRead (
        $id: Int!
      ) {
        markNotificationAsRead(id: $id)
      }
    `
  })
}

/**
 * The one member reached without a session: the unsubscribe link in an email carries its own key.
 */
const unsubscribe = (args: NotificationUnsubArgs): Promise<number> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation Unsubscribe (
        $unsubId: String!,
        $key: String!
      ) {
        unsubscribe (unsubId: $unsubId, key: $key)
      }
    `
  })
}

/**
 * Interface-satisfying stubs — see the note at the top of the file.
 */
const create = (_model: CreateNotificationDto): Promise<UserNotificationDto> => {
  throw new Error('Not implemented')
}

const update = (_model: UpdateNotificationDto): Promise<UserNotificationDto> => {
  throw new Error('Not implemented')
}

const deleteNotification = (_id?: number): Promise<any> => {
  throw new Error('Not implemented')
}

const deleteNotificationQueueItem = (notificationQueueItemId: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id: notificationQueueItemId
    },
    mutation: gql`
      mutation DeleteNotificationQueueItem (
        $id: Int!
      ) {
        deleteNotificationQueueItem (id: $id)
      }
    `
  })
}

const deleteAllNotificationQueueItems = (): Promise<any> => {
  return useApollo().mutate({
    mutation: gql`
      mutation DeleteAllNotificationQueueItems {
        deleteAllNotificationQueueItems
      }
    `
  })
}

const getUnreadNotificationCount = (args?: NotificationGetArgs): Promise<UnreadCountDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUnreadNotificationCount (
        $notificationCodes: [String!]
      ) {
        unreadNotificationCount (
          notificationCodes: $notificationCodes
        ) {
          author,
          reviewer
        }
      }
    `
  })
}

/**
 * `globalLoader` is chained by the list mixin (`globalLoader(x).get(...)`) and by `BaseApp`'s count
 * poll, so it hands back this bag rather than apollo's.
 */
const service = {
  ...crud,

  get,
  getById,
  markAsRead,
  unsubscribe,
  create,
  update,
  delete: deleteNotification,
  deleteNotificationQueueItem,
  deleteAllNotificationQueueItems,
  getUnreadNotificationCount,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function notificationService () {
  return service
}
